import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Stack, Typography, Button, Chip, Select, MenuItem, Tooltip, IconButton, TextField, Menu,
  CircularProgress,
} from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import { AppContext, AuthContext } from 'Context';
import { axios, baseUrl, endpoints } from 'Api';
import PillSearch from 'Components/PillSearch';
import { ConfirmDialog } from 'Components/ConfirmDialog';
import { AnimatedUserRoundPlus } from 'Components/reactbits/AnimatedIcons';
import useMembership from 'Pages/userManagement/MembershipDemo/useMembership';
import { V2SettingsShell } from '../../_shared/V2SettingsShell';
import MembershipPanelV2 from './MembershipPanelV2';

const ChevronDownIcon = (props) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <svg {...props} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const rowName = (u) => `${u?.firstname || ''} ${u?.lastname || ''}`.trim() || u?.email?.split('@')[0] || '—';
const planLabel = (u) => {
  const base = u?.subscription?.product_name || u?.plan_name || 'Free';
  return u?.expired === true ? `${base} (Expired)` : base;
};
const lastAccessedLabel = (u) => {
  if (!u?.last_accessed) return '—';
  const d = new Date(u.last_accessed);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const TH_SX = {
  textAlign: 'left', py: 1, px: 1, fontSize: 12, fontWeight: 700, color: '#9aa1ab', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap',
};
const TR_SX = { '& td': { py: 1.25, px: 1, fontSize: 14, verticalAlign: 'middle' }, '&:hover td': { bgcolor: '#f8f9fb' } };

// Row action icons — quiet until hovered, so the Access button stays the accent.
const ROW_ICON_SX = {
  color: '#6b7280',
  borderRadius: '10px',
  '&:hover': { color: '#15171c', bgcolor: 'rgba(21,23,28,0.06)' },
};

// Rounded white "pill" matching the dashboard search — reused for the plan filter.
const PILL_SELECT_SX = {
  minWidth: 128,
  height: 37,
  bgcolor: '#fff',
  fontSize: 13,
  fontWeight: 700,
  borderRadius: '999px',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0d5dd', borderRadius: '999px' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#15171c' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#15171c', borderWidth: 1 },
  '& .MuiSelect-select': { py: 0, display: 'flex', alignItems: 'center' },
};

// Affiliate membership tiers (upcoming) — the pill's colours per tier.
const TIERS = {
  bronze: { label: 'Bronze', from: '#b06a3a', to: '#e0a878', text: '#fff' },
  silver: { label: 'Silver', from: '#8b93a1', to: '#d7dce4', text: '#20242c' },
  gold: { label: 'Gold', from: '#c99a2e', to: '#f4d371', text: '#20242c' },
  platinum: { label: 'Platinum', from: '#3a4a5f', to: '#aab8ca', text: '#fff' },
};

export const UserManagementSettingsV2 = () => {
  const navigate = useNavigate();
  const { getToken } = useContext(AuthContext);
  const {
    setAppSnackbar, switchingFunction, isTokenChange, apiResponse, fetchData,
  } = useContext(AppContext);
  const membership = useMembership();

  const [referralKey, setReferralKey] = useState('');
  const [affiliateTier, setAffiliateTier] = useState('');
  const [copied, setCopied] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  const [managerUsers, setManagerUsers] = useState([]);
  const [connectedUsers, setConnectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessTarget, setAccessTarget] = useState(null);
  const [accessing, setAccessing] = useState(false);
  // Per-row action in flight (invite / copy signup link / hide-unhide).
  const [invitingId, setInvitingId] = useState(null);
  const [copyingUserId, setCopyingUserId] = useState(null);
  const [hidingId, setHidingId] = useState(null);

  const [tab, setTab] = useState('connected');
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  // Old modal's filter menu: "All users" vs "Hidden users" (visible === '0').
  const [visibilityFilter, setVisibilityFilter] = useState('all');
  const [createAnchor, setCreateAnchor] = useState(null);

  // Business name (inline editable).
  const [bizName, setBizName] = useState('');
  const [editingBiz, setEditingBiz] = useState(false);
  const [bizDraft, setBizDraft] = useState('');
  const [savingBiz, setSavingBiz] = useState(false);

  useEffect(() => {
    setBizName(apiResponse?.data?.[0]?.business_name || '');
  }, [apiResponse]);

  const loadUsers = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const token = await getToken();
      const [mgr, inv, ref] = await Promise.all([
        axios.post(`${baseUrl}/managerUsersList`, { token }).catch(() => null),
        axios.post(`${baseUrl}/inviteUsersList`, { token }).catch(() => null),
        axios.post(`${baseUrl}/getMyReferralDetails`, { token }).catch(() => null),
      ]);
      // Users = accounts I created / can access.
      setManagerUsers(Array.isArray(mgr?.data?.data) ? mgr.data.data : []);
      // Connected Account = users who can access MY account (inviteUsersList).
      const invData = inv?.data;
      const invList = Array.isArray(invData)
        ? invData
        : (invData?.data || invData?.users || []);
      setConnectedUsers(Array.isArray(invList) ? invList : []);
      setReferralKey(ref?.data?.referral_details?.referral_key || '');
      setAffiliateTier(
        (ref?.data?.referral_details?.membership || ref?.data?.referral_details?.tier || '').toString().toLowerCase(),
      );
    } catch (e) {
      if (!silent) { setManagerUsers([]); setConnectedUsers([]); }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { loadUsers(); }, [loadUsers, isTokenChange]);

  const referralLink = referralKey ? `https://www.projectorstream.com/referral/${referralKey}` : '';
  // The actual affiliate CODE (reference code), not the referral URL's key.
  const affiliateCode = apiResponse?.data?.[0]?.affiliateCode || '';
  const displayCode = affiliateCode || referralKey;
  const copyInvite = async () => {
    if (!referralLink) return;
    try {
      if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      if (setAppSnackbar) setAppSnackbar({ isVisible: true, type: 'success', message: 'Invite link copied to clipboard' });
    } catch (e) { /* noop */ }
  };
  // Copies the affiliate SIGNUP LINK carrying the reference code, e.g.
  // https://projector.app/newSign-up?reference_code=01026
  const affiliateSignupLink = displayCode ? `https://projector.app/newSign-up?reference_code=${displayCode}` : '';
  const copyCode = async () => {
    if (!affiliateSignupLink) return;
    try {
      if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(affiliateSignupLink);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 1600);
      if (setAppSnackbar) setAppSnackbar({ isVisible: true, type: 'success', message: 'Affiliate signup link copied' });
    } catch (e) { /* noop */ }
  };

  const saveBusiness = async () => {
    const next = (bizDraft || '').trim();
    if (!next) { setEditingBiz(false); return; }
    setSavingBiz(true);
    try {
      const token = await getToken();
      const { data } = await axios.post(`${baseUrl}/saveBusinessName`, { token, business_name: next });
      if (data?.success !== false) {
        setBizName(next);
        setEditingBiz(false);
        if (fetchData) fetchData();
        if (setAppSnackbar) setAppSnackbar({ isVisible: true, type: 'success', message: 'Business name updated' });
      } else if (setAppSnackbar) {
        setAppSnackbar({ isVisible: true, type: 'error', message: data?.message || 'Could not update business name' });
      }
    } catch (e) {
      if (setAppSnackbar) setAppSnackbar({ isVisible: true, type: 'error', message: 'Could not update business name' });
    } finally {
      setSavingBiz(false);
    }
  };

  const createUser = (userType) => {
    setCreateAnchor(null);
    navigate('/create-user', { state: { userType } });
  };

  // Connected Account = users who can access MY account (inviteUsersList);
  // Users = accounts I created / can access (managerUsersList).
  const tabs = [
    { key: 'connected', label: 'Connected Account', count: connectedUsers.length },
    { key: 'users', label: 'Users', count: managerUsers.length },
  ];
  // managerUsersList / inviteUsersList come back in creation order (oldest
  // first). The old users modal reversed them so the most recently added user
  // sits at the top of the list — keep that here.
  const baseRows = tab === 'connected'
    ? [...connectedUsers].reverse()
    : [...managerUsers].reverse();

  // Visibility filter, as the old modal's filter menu had it: "All users"
  // lists everything (hidden ones included), "Hidden users" only the ones
  // hidden via hideManagerUser. Hiding only applies to accounts I created,
  // so the filter is a users-tab thing.
  const hiddenCount = tab === 'users' ? baseRows.filter((u) => u?.visible === '0').length : 0;
  const activeRows = tab === 'users' && visibilityFilter === 'hidden'
    ? baseRows.filter((u) => u?.visible === '0')
    : baseRows;

  const planOptions = ['all', ...Array.from(new Set(activeRows.map(planLabel)))];
  const q = search.trim().toLowerCase();
  const shown = activeRows.filter((u) => {
    if (planFilter !== 'all' && planLabel(u) !== planFilter) return false;
    if (q) {
      const name = `${u?.firstname || ''} ${u?.lastname || ''}`.toLowerCase();
      if (!name.includes(q) && !(u?.email || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const exportCsv = () => {
    const header = ['Name', 'Email', 'Plan', 'Last accessed'];
    const lines = [header, ...shown.map((u) => [rowName(u), u?.email || '', planLabel(u), lastAccessedLabel(u)])]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([lines], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const base = tab === 'connected' ? 'connected-accounts' : 'users';
    a.download = `${base}${tab === 'users' && visibilityFilter === 'hidden' ? '-hidden' : ''}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  // ── Row actions, same as the old users list (ManagerList) ───────────────
  // Send invitation — optimistically flips the row to "invited" (signup_status
  // '2'), then reverts if the API says no.
  const sendInvite = async (u) => {
    if (!u?.userId) return;
    setInvitingId(u.userId);
    const setStatus = (status) => setManagerUsers((prev) => prev.map((row) => (
      row.userId === u.userId ? { ...row, signup_status: status } : row)));
    setStatus('2');
    try {
      const token = await getToken();
      const { data } = await axios.post(`${baseUrl}/sendInviteLink`, { token, userId: u.userId });
      if (data?.success) {
        setAppSnackbar({ isVisible: true, type: 'success', message: data?.message || 'Invitation sent successfully' });
        loadUsers({ silent: true });
      } else {
        setStatus('0');
        setAppSnackbar({ isVisible: true, type: 'error', message: data?.message || 'Failed to send invitation' });
      }
    } catch (e) {
      setStatus('0');
      setAppSnackbar({ isVisible: true, type: 'error', message: e?.response?.data?.message || 'Failed to send invitation' });
    } finally {
      setInvitingId(null);
    }
  };

  // Copy signup link. Safari only allows clipboard.write() synchronously inside
  // the gesture, so the fetch is handed to ClipboardItem as a promise; other
  // browsers fall back to writeText() after the await.
  const copySignupLink = (u) => {
    if (!u?.userId) return;
    setCopyingUserId(u.userId);
    const fetchLink = (async () => {
      const token = await getToken();
      const { data } = await axios.post(`${baseUrl}${endpoints.copySignupLink}`, { token, userId: u.userId });
      if (data?.success && data?.data?.short_url) return data.data.short_url;
      throw new Error(data?.message || 'Failed to get signup link');
    })();
    const ok = () => setAppSnackbar({ isVisible: true, type: 'success', message: 'Signup link copied to clipboard' });
    const fail = (e) => setAppSnackbar({ isVisible: true, type: 'error', message: e?.message || 'Failed to copy signup link' });

    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      const item = new ClipboardItem({
        'text/plain': fetchLink.then((url) => new Blob([url], { type: 'text/plain' })),
      });
      navigator.clipboard.write([item]).then(ok).catch(fail).finally(() => setCopyingUserId(null));
    } else {
      fetchLink
        .then((url) => navigator.clipboard.writeText(url))
        .then(ok)
        .catch(fail)
        .finally(() => setCopyingUserId(null));
    }
  };

  // Hide / unhide a user (visible === '0' means hidden).
  const toggleHideUser = async (u) => {
    if (!u?.userId) return;
    const hidden = u?.visible === '0';
    setHidingId(u.userId);
    try {
      const token = await getToken();
      const { data } = await axios.post(`${baseUrl}/hideManagerUser`, {
        token, user_id: u.userId, hide: hidden ? 0 : 1,
      });
      setAppSnackbar({
        isVisible: true,
        type: hidden ? 'success' : 'error',
        message: data?.message || (hidden ? 'User unhidden successfully' : 'User hidden'),
      });
      await loadUsers({ silent: true });
    } catch (e) {
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message: e?.response?.data?.message || `Failed to ${hidden ? 'unhide' : 'hide'} user`,
      });
    } finally {
      setHidingId(null);
    }
  };

  const accessUser = (u) => {
    if (u?.userId && u?.token && switchingFunction) setAccessTarget(u);
    else if (setAppSnackbar) setAppSnackbar({ isVisible: true, type: 'error', message: 'This account can\'t be accessed directly' });
  };
  const confirmAccess = async () => {
    if (!accessTarget) return;
    setAccessing(true);
    try {
      await switchingFunction(accessTarget);
    } catch (e) { /* switchingFunction surfaces its own errors */ } finally {
      setAccessing(false);
      setAccessTarget(null);
    }
  };

  // Membership badge + compact icon actions (create / copy invite / export).
  const HDR_ICON_BTN = {
    border: '1px solid #d0d5dd',
    borderRadius: 2,
    color: '#15171c',
    '&:hover': { borderColor: '#15171c', bgcolor: 'rgba(21,23,28,0.04)' },
  };
  const hdrTier = TIERS[affiliateTier] || TIERS.bronze;
  const hdrTierKnown = Boolean(TIERS[affiliateTier]);
  const headerAction = (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }} useFlexGap flexWrap="wrap">
      {/* {referralKey && (
        <Tooltip title="Membership tiers are coming soon">
          <Stack direction="row" spacing={0.5} sx={{ height: 32, alignItems: 'center', px: 1.5, borderRadius: 999, background: `linear-gradient(135deg, ${hdrTier.from}, ${hdrTier.to})`, color: hdrTier.text }}>
            <Typography sx={{ fontWeight: 800, fontSize: 12, letterSpacing: 0.3 }}>{hdrTier.label}</Typography>
            {!hdrTierKnown && <Box component="span" sx={{ fontSize: 9, fontWeight: 700, opacity: 0.85, textTransform: 'uppercase', ml: 0.25 }}>Upcoming</Box>}
          </Stack>
        </Tooltip>
      )} */}
      <Button
        variant="contained"
        size="small"
        disableElevation
        onClick={(e) => setCreateAnchor(e.currentTarget)}
        startIcon={<AnimatedUserRoundPlus size={16} />}
        sx={{ height: 32, textTransform: 'none', fontWeight: 700, borderRadius: 2, whiteSpace: 'nowrap', bgcolor: '#15171c', '&:hover': { bgcolor: '#000' } }}
      >
        Create
      </Button>
      <Menu anchorEl={createAnchor} open={Boolean(createAnchor)} onClose={() => setCreateAnchor(null)}>
        <MenuItem onClick={() => createUser('quick')} sx={{ fontSize: 14, fontWeight: 600 }}>Quick Set Up</MenuItem>
        <MenuItem onClick={() => createUser('withPlan')} sx={{ fontSize: 14, fontWeight: 600 }}>Full Service Set Up</MenuItem>
      </Menu>
    </Stack>
  );

  // Affiliate page title: "Your Affiliate Code: <code>" + copy-the-code icon.
  const titleNode = displayCode ? (
    <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
      <Box component="span" sx={{ color: 'text.secondary', fontWeight: 600 }}>Your Affiliate Code:</Box>
      <Box component="span" sx={{ fontWeight: 800, ml: 1 }}>{displayCode}</Box>
      <Tooltip title={codeCopied ? 'Copied' : 'Copy affiliate code'}>
        <IconButton size="small" onClick={copyCode} sx={{ ml: 0.25, color: 'text.secondary', '&:hover': { color: '#15171c' } }}>
          {codeCopied ? <CheckRoundedIcon fontSize="small" /> : <ContentCopyRoundedIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
    </Stack>
  ) : 'User management';

  return (
    <V2SettingsShell
      title={titleNode}
      description="Connected accounts and the users you've created or can access."
      headerAction={membership.isMember ? headerAction : undefined}
    >
      <MembershipPanelV2 {...membership} />
      {membership.isMember && (
      <Card sx={{ p: { xs: 1.5, md: 2.5 }, borderRadius: 2.5 }}>
        {/* Toolbar row: tabs (left) | search + plan filter (right). The
            affiliate code, membership + actions live in the page header now. */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          sx={{ justifyContent: 'space-between', alignItems: { md: 'center' }, mb: 1.5 }}
        >
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
            {/* Tabs (filter pill) */}
            <Stack direction="row" spacing={0.5} sx={{ bgcolor: 'action.hover', p: 0.5, borderRadius: 999 }}>
              {tabs.map((t) => {
                const active = tab === t.key;
                return (
                  <Button
                    key={t.key}
                    size="small"
                    disableElevation
                    onClick={() => { setTab(t.key); setPlanFilter('all'); setVisibilityFilter('all'); }}
                    variant={active ? 'contained' : 'text'}
                    sx={{ borderRadius: 999, minWidth: 0, px: 1.75, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap', color: active ? undefined : 'text.secondary' }}
                  >
                    {t.label}
                    <Box component="span" sx={{ ml: 0.75, opacity: 0.7, fontWeight: 700 }}>{t.count}</Box>
                  </Button>
                );
              })}
            </Stack>
          </Stack>

          {/* Search + plan filter + export, in the table toolbar. */}
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }} useFlexGap flexWrap="wrap">
            <PillSearch value={search} onChange={setSearch} placeholder="Search users" minWidth={180} />
            {tab === 'users' && (
              <Select
                value={visibilityFilter}
                onChange={(e) => { setVisibilityFilter(e.target.value); setPlanFilter('all'); }}
                size="small"
                IconComponent={ChevronDownIcon}
                renderValue={(v) => (
                  <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                    <FilterListRoundedIcon sx={{ fontSize: 18, color: v === 'hidden' ? '#15171c' : '#6b7280' }} />
                    <Box component="span">{v === 'hidden' ? 'Hidden users' : 'All users'}</Box>
                  </Stack>
                )}
                sx={PILL_SELECT_SX}
              >
                <MenuItem value="all" sx={{ fontSize: 13, fontWeight: 600 }}>All users</MenuItem>
                <MenuItem value="hidden" sx={{ fontSize: 13, fontWeight: 600 }}>
                  Hidden users
                  <Box component="span" sx={{ ml: 0.75, color: 'text.secondary', fontWeight: 700 }}>{hiddenCount}</Box>
                </MenuItem>
              </Select>
            )}
            <Select
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
              size="small"
              IconComponent={ChevronDownIcon}
              sx={PILL_SELECT_SX}
            >
              {planOptions.map((p) => (
                <MenuItem key={p} value={p} sx={{ fontSize: 13, fontWeight: 600 }}>{p === 'all' ? 'All plans' : p}</MenuItem>
              ))}
            </Select>
            <Tooltip title="Export current tab as CSV">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={exportCsv}
                  disabled={!shown.length}
                  startIcon={<FileDownloadRoundedIcon fontSize="small" />}
                  sx={{ height: 37, textTransform: 'none', fontWeight: 600, borderRadius: '999px', color: '#15171c', borderColor: '#d0d5dd', '&:hover': { borderColor: '#15171c', bgcolor: 'rgba(21,23,28,0.04)' } }}
                >
                  Export
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {loading ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 5, textAlign: 'center' }}>Loading users…</Typography>
        ) : !shown.length ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 5, textAlign: 'center' }}>
            {q || planFilter !== 'all' || visibilityFilter !== 'all'
              ? 'No users match your filters.'
              : `No ${tab === 'connected' ? 'connected accounts' : 'users'} yet.`}
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <Box component="thead">
                <Box component="tr" sx={{ '& th': TH_SX }}>
                  <Box component="th">Name</Box>
                  <Box component="th">Plan</Box>
                  <Box component="th">Last accessed</Box>
                  <Box component="th" sx={{ textAlign: 'right !important' }}>Action</Box>
                </Box>
              </Box>
              <Box component="tbody">
                {shown.map((u, idx) => {
                  const name = rowName(u);
                  const expired = u?.expired === true;
                  const canAccess = Boolean(u?.userId && u?.token);
                  // Invite + copy-link only make sense before the user has
                  // signed up ('0' = not invited yet, '2' = invited).
                  // (Both are user-tab actions — sendInviteLink / hideManagerUser
                  // act on accounts I created, not on connected accounts.)
                  const canInvite = tab === 'users' && Boolean(u?.userId)
                    && (u?.signup_status === '0' || u?.signup_status === '2');
                  const hidden = u?.visible === '0';
                  const canHide = tab === 'users' && Boolean(u?.userId) && u?.visible !== undefined;
                  return (
                    <Box component="tr" key={u?.userId || u?.email || idx} sx={TR_SX}>
                      <Box component="td">
                        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                          <Box sx={{
                            width: 32, height: 32, borderRadius: '50%', bgcolor: '#eef1f6', color: '#4b5563', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0, textTransform: 'uppercase', overflow: 'hidden',
                          }}
                          >
                            {u?.dpURL || u?.image ? (
                              <Box component="img" src={u.dpURL || u.image} alt={name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : name.charAt(0)}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#15171c' }} noWrap>{name}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }} noWrap>{u?.email}</Typography>
                          </Box>
                        </Stack>
                      </Box>
                      <Box component="td">
                        <Chip size="small" variant="outlined" color={expired ? 'default' : 'success'} label={planLabel(u)} sx={{ fontWeight: 600, height: 22 }} />
                      </Box>
                      <Box component="td">
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{lastAccessedLabel(u)}</Typography>
                      </Box>
                      <Box component="td" sx={{ textAlign: 'right' }}>
                        {/* Row actions, as the old users list had them: invite /
                            copy signup link (only while the user hasn't signed
                            up), hide-unhide, then Access. */}
                        <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
                          {canInvite && (
                            <>
                              <Tooltip title={u?.signup_status === '2' ? 'Resend invitation' : 'Send invitation'}>
                                <span>
                                  <IconButton size="medium" sx={ROW_ICON_SX} onClick={() => sendInvite(u)} disabled={invitingId === u.userId}>
                                    {invitingId === u.userId
                                      ? <CircularProgress size={20} color="inherit" />
                                      : <MailOutlineRoundedIcon sx={{ fontSize: 23 }} />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Copy signup link">
                                <span>
                                  <IconButton size="medium" sx={ROW_ICON_SX} onClick={() => copySignupLink(u)} disabled={copyingUserId === u.userId}>
                                    {copyingUserId === u.userId
                                      ? <CircularProgress size={20} color="inherit" />
                                      : <LinkRoundedIcon sx={{ fontSize: 23 }} />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>
                          )}
                          {canHide && (
                            <Tooltip title={hidden ? 'Unhide user' : 'Hide user'}>
                              <span>
                                <IconButton size="medium" sx={ROW_ICON_SX} onClick={() => toggleHideUser(u)} disabled={hidingId === u.userId}>
                                  {hidingId === u.userId
                                    ? <CircularProgress size={20} color="inherit" />
                                    : hidden
                                      ? <VisibilityRoundedIcon sx={{ fontSize: 23 }} />
                                      : <VisibilityOffRoundedIcon sx={{ fontSize: 23 }} />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {canAccess ? (
                            <Tooltip title={`Access ${name}'s account`}>
                              <span>
                                <IconButton size="medium" sx={ROW_ICON_SX} onClick={() => accessUser(u)}>
                                  <LoginRoundedIcon sx={{ fontSize: 23 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" sx={{ color: 'text.disabled', pl: 0.5 }}>—</Typography>
                          )}
                        </Stack>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}
      </Card>
      )}
      <ConfirmDialog
        open={Boolean(accessTarget)}
        title={accessTarget ? `Access ${rowName(accessTarget)}’s account?` : 'Access this account?'}
        description="You’ll switch into this account and act on their behalf until you exit."
        confirmLabel="Access account"
        cancelLabel="Cancel"
        loading={accessing}
        onConfirm={confirmAccess}
        onCancel={() => setAccessTarget(null)}
      />
    </V2SettingsShell>
  );
};
